import React, { useCallback, useRef, useState } from 'react';
import {
    AccordionSectionRoot,
    AccordionHeader,
    AccordionTitle,
    ToggleIcon,
    IconLine,
    AccordionContent,
    AccordionContentInner,
} from './styles';
import { v4 as uuidv4 } from 'uuid';

export type AccordionSectionProps = React.HTMLAttributes<HTMLDivElement> & {
    title?: React.ReactNode;
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    onToggle?: () => void;
    index?: number;
    toggleOpened?: (payload?: { open: boolean }) => void;
};

const AccordionSection: React.FC<AccordionSectionProps> = ({
    title,
    children,
    open = false,
    onOpen,
    onClose,
    onToggle,
    toggleOpened = () => {},
    ...otherProps
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<string | number>(0);

    const toggle = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        if (open) {
            setMaxHeight(`${el.offsetHeight}px`);
            setTimeout(() => setMaxHeight(0), 1);
            onClose?.();
        } else {
            setMaxHeight(`${el.offsetHeight}px`);
            setTimeout(() => setMaxHeight('none'), 250);
            onOpen?.();
        }
        onToggle?.();
        toggleOpened({ open: !open });
    }, [open, onClose, onOpen, onToggle, toggleOpened]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    };

    const sectionId = uuidv4();

    return (
        <AccordionSectionRoot {...otherProps}>
            <AccordionHeader
                id={`accordion-heading-${sectionId}`}
                aria-expanded={open}
                aria-controls={`accordion-heading-${sectionId}-content`}
                onClick={toggle}
                role="button"
                tabIndex={0}
                onKeyDown={onKeyDown}
            >
                <AccordionTitle className="accordion__title">{title}</AccordionTitle>
                <ToggleIcon className="accordion__icon" aria-hidden>
                    <IconLine className="accordion__icon__symbol-line accordion__icon__symbol-line--one" />
                    <IconLine className="accordion__icon__symbol-line accordion__icon__symbol-line--two" $open={open} />
                </ToggleIcon>
            </AccordionHeader>

            <AccordionContent
                className="accordion__content"
                id={`accordion-heading-${sectionId}-content`}
                role="region"
                aria-labelledby={`accordion-heading-${sectionId}`}
                $open={open}
                $maxHeight={maxHeight}
            >
                <AccordionContentInner className="accordion__content__inner" ref={ref}>
                    {children}
                </AccordionContentInner>
            </AccordionContent>
        </AccordionSectionRoot>
    );
};

export default AccordionSection;
